import { useEffect, useState } from "react";
import { Copy, LinkSimple, Prohibit } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* V9-B1 — Bloc minimal « Lien de recommandation » dans le drawer Apporteurs. */
export function PartnerLinkBlock({ apporteurName }) {
  const [state, setState] = useState({ loading: true, link: null, allowed: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/me/partner-link?apporteur=${encodeURIComponent(apporteurName)}`);
      setState({ loading: false, link: r.data.link, allowed: true });
    } catch (e) {
      setState({ loading: false, link: null, allowed: e?.response?.status !== 403 && e?.response?.status !== 404 });
    }
  };
  useEffect(() => { if (apporteurName) load(); }, [apporteurName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.loading || !state.allowed) return null;
  const link = state.link;
  const active = link?.status === "active";
  const url = link ? `${window.location.origin}${link.path}` : null;

  const create = async () => {
    setBusy(true);
    try { await api.post("/me/partner-link", { apporteur: apporteurName }); toast.success("Lien de recommandation créé"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/me/partner-link/disable", { apporteur: apporteurName, reason: "désactivé depuis la fiche apporteur" }); toast.success("Lien désactivé"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Désactivation impossible"); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 mt-3" data-testid="partner-link-block">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
          <LinkSimple size={13} weight="bold" /> Lien de recommandation
        </div>
        {link && (
          <span data-testid="partner-link-status" className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {active ? "Actif" : "Inactif"}
          </span>
        )}
      </div>
      {link ? (
        <>
          <div className="text-[12px] font-mono text-slate-700 break-all bg-slate-50 rounded-lg px-2 py-1.5" data-testid="partner-link-url">{url}</div>
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={copy} disabled={!active} className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40" data-testid="partner-link-copy">
              <Copy size={13} weight="bold" /> Copier
            </button>
            {active ? (
              <button type="button" onClick={disable} disabled={busy} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="partner-link-disable">
                <Prohibit size={13} weight="bold" /> Désactiver
              </button>
            ) : (
              <button type="button" onClick={create} disabled={busy} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="partner-link-recreate">
                Nouveau lien
              </button>
            )}
          </div>
        </>
      ) : (
        <button type="button" onClick={create} disabled={busy} className="w-full h-8 rounded-lg bg-slate-900 text-white text-xs font-semibold" data-testid="partner-link-create">
          Générer le lien personnel
        </button>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Les prospects arrivant par ce lien sont attribués automatiquement à cet apporteur (premier lien utilisé). Aucune donnée de dossier n'est visible via le lien.</p>
    </div>
  );
}
